import React, { useState } from 'react';
import { Menu, X, Home, Mail, User, Scale, FileText, Users, Award, Flag } from 'lucide-react';

function App() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [language, setLanguage] = useState('tr'); // 'tr' for Turkish, 'en' for English

    const toggleLanguage = () => {
        setLanguage(language === 'tr' ? 'en' : 'tr');
    };

    const texts = {
        tr: {
            home: 'Ana Sayfa',
            about: 'Hakkında',
            practiceAreas: 'Uygulama Alanları',
            contact: 'İletişim',
            title: 'Ubi ius ibi remedium',
            subtitle: '"Where there is law, there is remedy"',
            attorney: 'Avukat Bensu Koç',
            phone: 'Telefon no: 0539 725 33 15',
            officePhone: 'Ofis Telefon no: 0312 248 13 00',
            email: 'E-Posta: bkoc@bensukoc.av.tr',
            address: 'Adres: Via Flat İş Merkezi Kat:3, Beştepe, Nergiz Sk. No:7-2, 06560 Yenimahalle/Ankara',
            practiceAreasTitle: 'Uygulama Alanları',
            getInTouch: 'İletişime Geçin',
            practiceAreasSubtitle: 'Birçok hukuk alanında kapsamlı hukuki hizmetler',
            civilLaw: 'Medeni Hukuk',
            civilLawDesc: 'Kişilik hakları, eşya hukuku, borçlar ve sözleşmeler konusunda kapsamlı hukuki destek.',
            commercialLaw: 'Ticaret Hukuku',
            commercialLawDesc: 'Şirket kuruluşu, ticari sözleşmeler ve iş uyuşmazlıklarında uzman hukuki danışmanlık.',
            familyLaw: 'Aile Hukuku',
            familyLawDesc: 'Boşanma, velayet, nafaka ve diğer aile hukukuna ilişkin davalarda hassas yaklaşım.',
            criminalLaw: 'Ceza Hukuku',
            criminalLawDesc: 'Ceza davalarında savunma ve mağdur haklarının korunmasında etkin hukuki temsil.',
        },
        en: {
            home: 'Home',
            about: 'About',
            practiceAreas: 'Practice Areas',
            contact: 'Contact',
            title: 'Ubi ius ibi remedium',
            subtitle: '"Where there is law, there is remedy"',
            attorney: 'Attorney Bensu Koç',
            phone: 'Phone: +90 0539 725 33 15',
            email: 'Email: bkoc@bensukoc.av.tr',
            officePhone: 'Office Phone: +90 0312 248 13 00',
            address: 'Address: Via Flat İş Merkezi Kat:3, Beştepe, Nergiz Sk. No:7-2, 06560 Yenimahalle/Ankara',
            practiceAreasTitle: 'Practice Areas',
            getInTouch: 'Get in Touch',
            practiceAreasSubtitle: 'Comprehensive legal services across multiple areas of law',
            civilLaw: 'Civil Law',
            civilLawDesc: 'Personal rights, property law, obligations, and contract law with comprehensive legal support.',
            commercialLaw: 'Commercial Law',
            commercialLawDesc: 'Company formation, commercial contracts, and business dispute resolution with expert guidance.',
            familyLaw: 'Family Law',
            familyLawDesc: 'Divorce proceedings, custody matters, alimony, and family legal issues handled with sensitivity.',
            criminalLaw: 'Criminal Law',
            criminalLawDesc: 'Criminal defense and victim rights protection with dedicated legal representation.',
        },
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        {/* Logo/Brand - Left Side */}
                        <div className="flex-shrink-0">
                            <h1 className="text-3xl font-light text-red-700 tracking-wide" style={{ fontFamily: 'Redressed, cursive' }}>
                                BK | Bensu Koç
                            </h1>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex space-x-8 items-center">
                            <a href="#home" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium">
                                <Home size={18} />
                                <span>{texts[language].home}</span>
                            </a>
                            <a href="#about" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium">
                                <User size={18} />
                                <span>{texts[language].about}</span>
                            </a>
                            <a href="#practice-areas" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium">
                                <Scale size={18} />
                                <span>{texts[language].practiceAreas}</span>
                            </a>
                            <a href="#contact" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium">
                                <Mail size={18} />
                                <span>{texts[language].contact}</span>
                            </a>

                            {/* Language Switcher */}
                            <button
                                onClick={toggleLanguage}
                                className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium"
                            >
                                <Flag size={18} />
                                <span>{language === 'tr' ? 'TR' : 'EN'}</span>
                            </button>
                        </nav>

                        {/* Mobile menu button */}
                        <div className="md:hidden flex items-center space-x-4">
                            <button
                                onClick={toggleLanguage}
                                className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors duration-200"
                            >
                                <Flag size={18} />
                                <span>{language === 'tr' ? 'TR' : 'EN'}</span>
                            </button>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="text-gray-700 hover:text-red-600 transition-colors duration-200"
                            >
                                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Navigation */}
                    {isMenuOpen && (
                        <div className="md:hidden py-4 border-t border-gray-200">
                            <div className="flex flex-col space-y-4">
                                <a href="#home" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 font-medium px-2 py-1">
                                    <Home size={18} />
                                    <span>{texts[language].home}</span>
                                </a>
                                <a href="#about" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 font-medium px-2 py-1">
                                    <User size={18} />
                                    <span>{texts[language].about}</span>
                                </a>
                                <a href="#practice-areas" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 font-medium px-2 py-1">
                                    <Scale size={18} />
                                    <span>{texts[language].practiceAreas}</span>
                                </a>
                                <a href="#contact" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 font-medium px-2 py-1">
                                    <Mail size={18} />
                                    <span>{texts[language].contact}</span>
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                     style={{ backgroundImage: 'url(/image.png)' }}>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-950 to-amber-900 opacity-80"></div>
                <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
                    <h2 className="text-5xl md:text-6xl lg:text-7xl font-light mb-6 tracking-wide" style={{ fontFamily: 'serif' }}>
                        {texts[language].title}
                    </h2>
                    <p className="text-xl md:text-2xl font-light mb-12 text-red-100 italic">
                        {texts[language].subtitle}
                    </p>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h3 className="text-4xl font-light text-gray-800 mb-8" style={{ fontFamily: 'serif' }}>
                                {texts[language].attorney}
                            </h3>
                            <div className="text-gray-600 text-lg leading-relaxed mb-6">
                                <p>{texts[language].phone}</p>
                                <p>{texts[language].officePhone}</p>
                                <p>{texts[language].email}</p>
                                <p>{texts[language].address}</p>
                            </div>
                        </div>
                        <div className="relative">
                            <img src="/BensuSiteFoto.jpg" alt="Attorney Bensu Koç" className="w-full h-[800px] object-cover rounded-lg shadow-xl" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Practice Areas Section */}
            <section id="practice-areas" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h3 className="text-4xl font-light text-gray-800 mb-6" style={{ fontFamily: 'serif' }}>
                            {texts[language].practiceAreasTitle}
                        </h3>
                        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            {texts[language].practiceAreasSubtitle}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg border-t-4 border-red-600">
                            <Scale className="text-red-600 mb-4" size={24} />
                            <h4 className="text-xl font-semibold text-gray-800 mb-4">{texts[language].civilLaw}</h4>
                            <p className="text-gray-600">{texts[language].civilLawDesc}</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg border-t-4 border-red-600">
                            <FileText className="text-red-600 mb-4" size={24} />
                            <h4 className="text-xl font-semibold text-gray-800 mb-4">{texts[language].commercialLaw}</h4>
                            <p className="text-gray-600">{texts[language].commercialLawDesc}</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg border-t-4 border-red-600">
                            <Users className="text-red-600 mb-4" size={24} />
                            <h4 className="text-xl font-semibold text-gray-800 mb-4">{texts[language].familyLaw}</h4>
                            <p className="text-gray-600">{texts[language].familyLawDesc}</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg border-t-4 border-red-600">
                            <Award className="text-red-600 mb-4" size={24} />
                            <h4 className="text-xl font-semibold text-gray-800 mb-4">{texts[language].criminalLaw}</h4>
                            <p className="text-gray-600">{texts[language].criminalLawDesc}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h4 className="text-2xl font-light mb-4" style={{ fontFamily: 'serif' }}>
                        BK | Bensu Koç
                    </h4>
                    <p className="text-gray-300 mb-6">Professional legal services with integrity and excellence</p>
                    <div className="flex justify-center space-x-6">
                        <a href="#" className="text-gray-300 hover:text-white">Privacy Policy</a>
                        <a href="#" className="text-gray-300 hover:text-white">Terms of Service</a>
                        <a href="#" className="text-gray-300 hover:text-white">Contact</a>
                    </div>
                    <div className="mt-8 pt-8 border-t border-gray-700 text-gray-400">
                        <p>&copy; 2025 Attorney Bensu Koç. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default App;